import GavelIcon from '@mui/icons-material/Gavel';
import HomeIcon from '@mui/icons-material/Home';
import BusinessIcon from '@mui/icons-material/Business';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import PictureInPictureIcon from '@mui/icons-material/PictureInPicture';
import GoogleIcon from '@mui/icons-material/Google';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import CircleNotificationsIcon from '@mui/icons-material/CircleNotifications';
import HistoryIcon from '@mui/icons-material/History';
import RssFeedIcon from '@mui/icons-material/RssFeed';
import BugReportIcon from '@mui/icons-material/BugReport';
import SettingsIcon from '@mui/icons-material/Settings';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import DnsIcon from '@mui/icons-material/Dns';
import InsightsIcon from '@mui/icons-material/Insights';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import dayjs from 'dayjs';
import { differenceInDays, formatDistanceToNow } from 'date-fns';
import { Skeleton, TableCell, Typography } from '@mui/material';
import { Loader } from '../components';
import { GSBStatus, GSBStatusId, MenuItem } from '../helpers/types';
import { Keyword } from '../types/KeywordsTypes';

export const PER_PAGE_RECORDS = 100;
export const PER_PAGE_COMPANY_RECORDS = 25;
export const DEFAULT_PER_PAGE = 25;

export const menuItems: MenuItem[] = [
  {
    id: 1,
    menuName: 'Home',
    icon: <HomeIcon />,
    to: '/dashboard',
  },
  {
    id: 2,
    menuName: 'Customer Review Request',
    icon: <PendingActionsIcon />,
    to: '/threats/customer-review-requests',
  },
  {
    id: 3,
    menuName: 'Takedown',
    icon: <GavelIcon />,
    to: '/takedown',
  },
  {
    id: 12,
    menuName: 'FN Discovery',
    icon: <BugReportIcon />,
    to: '/fn-discovery',
  },
  {
    id: 15,
    menuName: 'Domain Lookup',
    icon: <TravelExploreIcon />,
    to: '/domain-lookup',
  },
  {
    id: 4,
    menuName: 'Companies',
    icon: <BusinessIcon />,
    to: '/companies',
  },
  {
    id: 5,
    menuName: 'Historical Scan',
    icon: <HistoryIcon />,
    to: '/historical-scan',
  },
  {
    id: 6,
    menuName: 'Network Disruption',
    icon: <CircleNotificationsIcon />,
    to: '/network-disruption',
  },
  {
    id: 7,
    menuName: 'False Positive',
    icon: <PictureInPictureIcon />,
    to: '/false-positive',
  },
  {
    id: 8,
    menuName: 'GSB',
    icon: <GoogleIcon />,
    to: '/gsb',
  },
  {
    id: 9,
    menuName: 'Feeds',
    icon: <RssFeedIcon />,
    to: '/feeds',
  },
  {
    id: 10,
    menuName: 'CTI Graph',
    icon: <AccountTreeIcon />,
    to: '/cti-graph',
  },
  {
    id: 13,
    menuName: 'Bulk Actions',
    icon: <PlaylistAddCheckIcon />,
    to: '/bulk-actions',
  },
  {
    id: 14,
    menuName: 'AI Stats',
    icon: <InsightsIcon />,
    to: '/threat-explain-stats',
    flag: 'threat-explain-stats',
  },
  {
    id: 11,
    menuName: 'Settings',
    icon: <SettingsIcon />,
    to: '/settings/registrars-table',
    submenu: [
      {
        id: 112,
        menuName: 'Registrars',
        icon: <DnsIcon />,
        to: '/settings/registrars-table',
      },
      {
        id: 113,
        menuName: 'Nameservers',
        icon: <DnsIcon />,
        to: '/settings/nameservers',
      },
      {
        id: 114,
        menuName: 'TLDs',
        icon: <DnsIcon />,
        to: '/settings/tlds',
      },
    ],
  },
];

export const EXTERNAL_THREAT_PENDING_STATUS = 'external-threat-pending';

// Statuses that reject a threat. Setting one on an external-threat-pending
// threat needs a rejection reason, and bulk changes skip those threats
// (WHEEL-404: benign follows discarded).
export const REJECTION_STATUSES = ['discarded', 'benign'];

export const isRejectionStatus = (status?: string): boolean =>
  !!status && REJECTION_STATUSES.includes(status);

export const threatsStatus = [
  'discarded',
  'benign',
  'confirmed',
  'trademark',
  'owned',
  'pre-confirmed',
  'detected',
];

export const statusList: any = [
  { Id: 1, Name: 'detected' },
  { Id: 2, Name: 'confirmed' },
  { Id: 3, Name: 'discarded' },
  { Id: 6, Name: 'trademark' },
  { Id: 7, Name: 'owned' },
  { Id: 5, Name: 'pre-confirmed' },
  { Id: 8, Name: 'pre-confirmed-by-matrix' },
  { Id: 9, Name: 'monitored' },
  { Id: 10, Name: 'pre-confirmed-by-brp' },
  // Prod id; dev/staging use 22. Only Name is read from this fallback.
  { Id: 21, Name: 'benign' },
];

export const options = [
  { value: 'detected', label: 'Detected' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'discarded', label: 'Discarded' },
  { value: 'trademark', label: 'Trademark' },
  { value: 'owned', label: 'Owned' },
  { value: 'pre-confirmed', label: 'Pre-confirmed' },
  { value: 'pre-confirmed-by-matrix', label: 'Pre-confirmed-by-matrix' },
  { value: 'monitored', label: 'monitored' },
  { value: 'pre-confirmed-by-brp', label: 'pre-confirmed-by-brp' },
];

export const riskData = ['high', 'medium', 'low'];

export const getSourceColor = (source?: string): string => {
  switch (source) {
    case 'client_panel':
      return '#6366F1'; // indigo
    case 'daily_scan':
      return '#0EA5E9'; // sky blue
    case 'favicon_detection':
      return '#F59E0B'; // amber
    case 'historical_scan':
      return '#F97316'; // orange
    case 'logo_detection':
      return '#14B8A6'; // teal
    case 'matrix':
      return '#8B5CF6'; // violet
    case 'false_negative':
      return '#EF4444'; // red
    case 'onboarding_scan':
      return '#10B981'; // emerald
    case 'resurgence_monitor':
      return '#D946EF'; // fuchsia
    case 'wheel':
      return '#EC4899'; // pink
    default:
      return '#667085'; // neutral gray
  }
};

export const getStatusColor = (status?: any) => {
  return '#667085';
  // remove this code after we decide if we use color or not for different statuses

  if (status === 'confirmed') {
    return '#5AA311';
  } else if (status === 'detected') {
    return '#00B2FF';
  } else if (status === 'discarded') {
    return '#F99D4D';
  } else if (status === 'trademark') {
    return '#885EC0';
  } else if (status === 'owned') {
    return '#25badb';
  } else if (status === 'pre-confirmed') {
    return '#885EC0';
  } else if (status === 'pre-confirmed-by-matrix') {
    return '#885EC0';
  } else if (status === 'monitored') {
    return '#1976d2';
  } else if (status === 'pre-confirmed-by-brp') {
    return '#885EC0';
  }
  return '#50CD89';
};

export const DNSRecordsHeader = ['Record Type', 'Address', 'Domain Name'];

export const SSLCertificateHeader = [
  'Domain',
  'Issuer',
  'Organisation',
  'Registered On',
  'Not After',
];

export const TakedownListHeader = [
  'User',
  'Created On',
  'Updated At',
  'Status',
  'Messsage',
];

export const GSBHistoryHeader = [
  'Id',
  'Domain',
  'URL',
  'Platform',
  'Region',
  'Labels',
  'Comments',
  'Abuse Type',
  'Created On',
  'Operation Id',
  'Status',
];
export const StatusHistoryHeader = ['Status', 'Source', 'User', 'Timestamp'];

export const a11yProps = (index: number) => {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
};

export const getLabelsAndIds = (
  filterValues: any[],
  property: string | number,
) => {
  return filterValues?.reduce((accumulator, currentItem) => {
    accumulator?.push(currentItem[property]);
    return accumulator;
  }, []);
};

export const defaultStatusLabel = [{ label: 'confirmed' }];

export const defaultStartDate = dayjs()
  .subtract(1, 'month')
  .startOf('day')
  .format('YYYY-MM-DD HH:mm:ss');

export const defaultEndDate = dayjs()
  .endOf('day')
  .format('YYYY-MM-DD HH:mm:ss');

export const defaultStartDateTakedown = dayjs()
  .subtract(4, 'day')
  .startOf('day')
  .format('YYYY-MM-DD HH:mm:ss');

export const defaultEndDateTakedown = dayjs()
  .endOf('day')
  .format('YYYY-MM-DD HH:mm:ss');

export const defaultSortBy = 'updated_at';
export const defaultSortOrder = 'DESCENDING';

export const companyStatusList = [
  { value: 'active', label: 'active' },
  { value: 'deleted', label: 'deleted' },
  { value: 'deactivated', label: 'deactivated' },
  { value: 'onboardingPending', label: 'onboardingPending' },
  { value: 'onboarded', label: 'onboarded' },
  { value: 'error', label: 'error' },
  { value: 'owned', label: 'owned' },
];

export const formatedDate = (date: string | number | Date) => {
  return new Date(date)
    .toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    })
    .replace(/\//g, '-');
};

export const validatePassword = (password: any) => {
  const minLength = 12;
  const hasUpperCase = /[A-Z]/.test(password);
  const hasLowerCase = /[a-z]/.test(password);
  const hasDigit = /\d/.test(password);

  if (
    password.length < minLength ||
    !hasUpperCase ||
    !hasLowerCase ||
    !hasDigit
  ) {
    return 'Password too easy. The password must contain at least 12 characters with, at least, 1 digit, 1 uppercase and 1 lowercase letter.';
  }
  return '';
};

export const validateEmail = (email: string) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    return 'Invalid email format.';
  }
  return '';
};

export const formatDateActivity = (date: string) => {
  const dateObj = new Date(date);
  const daysDifference = differenceInDays(new Date(), dateObj);
  if (daysDifference > 31) {
    return `${Math.floor(daysDifference / 30)} month(s) ago`;
  }
  return `${formatDistanceToNow(dateObj)} ago`;
};

export const NoDataMessage = ({ message }: { message: string }) => (
  <TableCell colSpan={12} align="center" sx={{ border: 0, py: 6 }}>
    <Typography variant="subtitle1">{message}</Typography>
  </TableCell>
);

export const LoaderWrapper = ({ size }: { size: number }) => (
  <TableCell
    colSpan={12}
    align="center"
    sx={{ border: 0, py: 6, alignContent: 'center' }}
  >
    <Loader size={size} />
  </TableCell>
);

export { exportToCSV } from './csvExportUtils';

export const areDifferentSets = (arr1: number[], arr2: number[]) => {
  if (arr1.length !== arr2.length) return true;

  const sortedA = [...arr1].sort();
  const sortedB = [...arr2].sort();

  for (let i = 0; i < sortedA.length; i++) {
    if (sortedA[i] !== sortedB[i]) return true;
  }

  return false;
};

export const activeDefense = [
  {
    domain: 'facebook.safe-transfer-link.com',
    created_by: 'Unknown',
    created_date: '04 Jul 2024, 11:55 AM',
  },
  {
    domain: 'nanliteglobal.ru',
    created_by: 'Nelson Alikou',
    created_date: '04 Jul 2024, 11:44 AM',
  },
  {
    domain: 'test.metamenon.com',
    created_by: 'Nakul K',
    created_date: '14 Jun 2024, 01:01 PM',
  },
  {
    domain: 'meta-advertising-business.com',
    created_by: 'Nakul K',
    created_date: '12 Jan 2024, 07:43 AM',
  },
  {
    domain: 'nanliteglobal.ru',
    created_by: 'Nelson Alikou',
    created_date: '04 Jul 2024, 11:44 AM',
  },
  {
    domain: 'test.metamenon.com',
    created_by: 'Nakul K',
    created_date: '14 Jun 2024, 01:01 PM',
  },
  {
    domain: 'meta-advertising-business.com',
    created_by: 'Nakul K',
    created_date: '12 Jan 2024, 07:43 AM',
  },
];

export const falsePositive = [
  {
    domain: 'betpublicamp27.com',
    created_by: 'Abu Qureshi',
    created_date: '	26 Aug 2024, 02:10 PM',
  },
  {
    domain: 'nanliteglobal.ru',
    created_by: 'Nelson Alikou',
    created_date: '04 Jul 2024, 11:44 AM',
  },
  {
    domain: 'dog4move.online',
    created_by: 'Keerthi Chevuturi',
    created_date: '26 Aug 2024, 02:10 PM',
  },
  {
    domain: 'the-coven.fr',
    created_by: 'Keerthi Chevuturi',
    created_date: '12 Jan 2024, 07:43 AM',
  },
  {
    domain: 'nanliteglobal.ru',
    created_by: 'Nelson Alikou',
    created_date: '04 Jul 2024, 11:44 AM',
  },
  {
    domain: 'test.metamenon.com',
    created_by: 'Keerthi Chevuturi',
    created_date: '14 Jun 2024, 01:01 PM',
  },
  {
    domain: 'empowermindsetmarket.store',
    created_by: 'Nakul K',
    created_date: '26 Aug 2024, 02:10 PM',
  },
];

export const disableAfterToday = (date: Date) => {
  const today = new Date();
  today.setHours(23, 59, 59, 999);
  return date.getTime() > today.getTime();
};

export const disableBforeToday = (date: Date) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return date.getTime() < today.getTime();
};

export const disableBefore365Days = (date: Date, selectedDate: Date) => {
  const normalizedSelectedDate = new Date(selectedDate);
  normalizedSelectedDate.setHours(0, 0, 0, 0);

  const minDate = new Date(normalizedSelectedDate);
  minDate.setDate(minDate.getDate() - 365);

  return date.getTime() < minDate.getTime();
};

// fallback data on API fail
export const SourcesData = [
  { label: 'Client Panel', value: 'client_panel' },
  { label: 'Daily Scan', value: 'daily_scan' },
  { label: 'Favicon Detection', value: 'favicon_detection' },
  { label: 'Historical Scan', value: 'historical_scan' },
  { label: 'Logo Detection', value: 'logo_detection' },
  { label: 'Matrix', value: 'matrix' },
  { label: 'False Negative', value: 'false_negative' },
  { label: 'Onboarding Scan', value: 'onboarding_scan' },
  { label: 'Resurgence Monitor', value: 'resurgence_monitor' },
  { label: 'Wheel', value: 'wheel' },
];

export const formatSourceName = (sourceName: string): string => {
  return sourceName
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export interface SourceOption {
  label: string;
  value: string;
}

// Single source of truth for the Sources filter options: the API list when
// available, the static SourcesData fallback otherwise. Used by both the
// FilterCard dropdown and the URL param parser - resolving URL values against
// a different list than the dropdown silently drops newer sources (WHEEL-368).
export const buildSourceOptions = (
  sourceList?: { name: string }[],
): SourceOption[] => {
  if (sourceList && sourceList.length > 0) {
    return sourceList.map(source => ({
      label: formatSourceName(source.name),
      value: source.name,
    }));
  }
  return SourcesData.map(source => ({
    label: source.label,
    value: source.value,
  }));
};

export const formatServiceName = (name: string): string => {
  return name.replace(/^Precrime\s+/i, '').replace(/Priority/gi, 'Prio');
};

export const isValidUrl = (str: string): boolean => {
  const regex = /^(https?|ftp):\/\/[^\s/$.?#].[^\s]*$/i;
  return regex.test(str);
};

export const getFormattedDescription = (
  description: string | undefined,
): string => {
  if (description) {
    return description.length > 30
      ? `${description.slice(0, 30)}...`
      : description;
  }
  return '-';
};

export const findGSBStatus = (
  id: number,
  statusData: GSBStatusId['data'],
): string =>
  statusData?.find((status: GSBStatus) => status?.id === id)?.name || 'Unknown';
export const defaultServiceFilter = [
  {
    id: 7,
    label: 'Precrime Brand - POC - High Priority',
  },
  {
    id: 5,
    label: 'Precrime Brand - Active Paying Customer',
  },
];

export const defaultStatusFilter = [
  { id: 1, label: 'detected' },
  { id: 8, label: 'pre-confirmed-by-matrix' },
  { id: 10, label: 'pre-confirmed-by-brp' },
];

export const defaultReviewStatusFilter = [
  { id: 17, label: 'owned-review' },
  { id: 14, label: 'discarded-review' },
  { id: 20, label: 'trademark-review' },
];

export const getDateOrDefault = (dateArray: string, defaultDate: string) => {
  return dateArray.length === 0 ? defaultDate : dateArray;
};

export const COMPLETION_REMARKS_OTHER_VALUE = 'others';

export const COMPLETION_REMARKS = [
  { value: 'client_hold', label: 'ClientHold' },
  { value: 'server_hold', label: 'ServerHold' },
  {
    value: 'content_removed',
    label: 'Content Removed, Records Dropped/Parked',
  },
  { value: 'nxdomain', label: 'NXDomain' },
  { value: 'account_suspended', label: 'Account Suspended' },
  { value: 'domain_purged', label: 'Domain Purged/Deleted' },
  { value: COMPLETION_REMARKS_OTHER_VALUE, label: 'Others' },
];

export const THREAT_TYPE_OPTIONS = [
  { value: 'Impersonation', label: 'Impersonation' },
  { value: 'Fraud', label: 'Fraud' },
  { value: 'Brand harm', label: 'Brand harm' },
  { value: 'Harassment', label: 'Harassment' },
  { value: 'Other', label: 'Other' },
];

export const TakeDownTypes = {
  identifiedThreatDomain: 'identified_threat_domain',
  externalThreatDomain: 'external_threat_domain',
  socialMedia: 'social_media',
};

export const TakeDownTypeList = [
  TakeDownTypes.identifiedThreatDomain,
  TakeDownTypes.externalThreatDomain,
  TakeDownTypes.socialMedia,
];

export const formatLabel = (str: string) => {
  return str
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const convertToUnixTimestamp = (date: Date): number => {
  return Math.floor(date.getTime() / 1000);
};

const dateDiffInHours = (date: string) => {
  const date1 = dayjs(date);
  const date2 = dayjs();
  return date2.diff(date1) / 3600000;
};

export const isRecentNetworkRecords = (type: string, date: string) => {
  const timeDiff = dateDiffInHours(date);
  return timeDiff < 24;
};

export const getExternalUrl = (type: string, name: string) => {
  switch (type) {
    case 'urlscan':
      return `https://urlscan.io/domain/${name}`;
    case 'archive':
      return `https://web.archive.org/web/20220000000000*/${name}`;
    case 'transparencyreport':
      return `https://transparencyreport.google.com/safe-browsing/search?url=${name}`;
    case 'crt':
      return `https://crt.sh/?q=${name}`;
    case 'virustotal':
      return `https://www.virustotal.com/gui/domain/${name}`;
    case 'threatintelligenceplatform':
      return `https://threatintelligenceplatform.com/report/${name}/vnrzbY2T1F`;
    case 'dnschecker':
      return `https://dnschecker.org/#A/${name}`;
    default:
      return '';
  }
};

export const defaultSourcesFilter = SourcesData.filter(
  source => source.label === 'Daily Scan',
);

// BRP classifier verdict/confidence filter options (ENG-718 / REQ-4).
export const BRP_VERDICT_OPTIONS = [
  { label: 'Threat', value: 'threat' },
  { label: 'Suspicious', value: 'suspicious' },
  { label: 'Benign', value: 'benign' },
  { label: 'Customer Owned', value: 'customer_owned' },
  { label: 'Insufficient Data', value: 'insufficient_data' },
];

export const BRP_CONFIDENCE_OPTIONS = [
  { label: 'High', value: 'high' },
  { label: 'Medium', value: 'medium' },
  { label: 'Low', value: 'low' },
];

export const defaultBrpVerdictFilter: { label: string; value: string }[] = [];
export const defaultBrpConfidenceFilter: { label: string; value: string }[] =
  [];

export const isDisabledStatus = (
  statusName: string,
  isCustomerReviewPage: boolean,
) => {
  const customerReviewStatus = [
    'trademark-review',
    'owned-review',
    'discarded-review',
  ];

  if (isCustomerReviewPage) {
    return !customerReviewStatus.includes(statusName);
  }

  return customerReviewStatus.includes(statusName);
};

export const urlRegex =
  /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/[^\s]*)?$/i;

export const formatDate = (date: any) => {
  if (!date) return null;
  return dayjs(date).format('YYYY-MM-DD HH:mm:ss');
};

export const calculateLastWeekDate = (): Date => {
  const today = new Date();
  today.setDate(today.getDate() - 6);
  return today;
};

export const removeExpiredNotifications = (notifications: any[]) => {
  const currentTime = new Date().toUTCString();
  const currentTimeMs = new Date(currentTime).getTime();
  return notifications.filter(notification => {
    const notificationTime = new Date(notification.date).getTime();
    return currentTimeMs - notificationTime <= 7 * 24 * 60 * 60 * 1000;
  });
};

export const MAX_NOTIFICATIONS = 1000;

export enum NETWORK_DATA_TYPE {
  WHOIS = 'whois',
  CERTIFICATE = 'certificate',
  RECORDS = 'records',
}

export const apiErrorMessage = (err: any): string => {
  if (typeof err?.response?.data?.detail?.[0]?.msg === 'string') {
    return err.response.data.detail[0].msg;
  }
  if (typeof err?.response?.data?.detail === 'string') {
    return err.response.data.detail;
  }
  if (typeof err?.response?.data?.detail?.message === 'string') {
    return err.response.data.detail.message;
  }
  return 'Something went wrong';
};

export const sanitizeText = (text: string): string => {
  return text
    .replace(/<[^>]*>/g, '')
    .replace(/[<>]/g, '')
    .trim();
};

export const getErrorDescriptionForProtectedDomain = (type: string): string => {
  switch (type) {
    case 'invalid_domain':
    case 'duplicate_protection':
      return 'Some domains couldnâ€™t be processed due to errors. Check the table for details and try again.';
    case 'invalid_csv_status':
      return 'The CSV contains invalid status names. Please restart the process using the provided template.';
    case 'invalid_csv_headers':
      return 'The CSV contains invalid headers names. Please restart the process using the provided template.';
    case 'invalid_csv_format':
      return 'The CSV contains invalid column names. Please restart the process using the provided template.';
    default:
      return 'There was a problem adding a new domains. Please restart the process.';
  }
};

export const modifyArrayObject = (items: { id: string; name: string }[]) => {
  return items?.map((item: { id: string; name: string }) => ({
    value: item?.id?.toString(),
    label: item.name,
  }));
};

export const renderSkeleton = (width = '50%', height = 40) => {
  return (
    <Skeleton
      animation="wave"
      variant="text"
      sx={{ fontSize: '20px' }}
      width={width}
      height={height}
    />
  );
};

export const SALES_USER_ROLES = [
  { label: 'Company Admin', value: 'company-admin', roleId: 2 },
  { label: 'Company Viewer', value: 'company-viewer', roleId: 21 },
  { label: 'Client Panel User', value: 'client_panel_user', roleId: 15 },
];

export const HISTORICAL_SCAN_TAB_TABLE_HEADERS = [
  { label: 'Dates', key: 'dateRequested' },
  { label: 'Status', key: 'status' },
  { label: 'Scanned Domains', key: 'scannedDomains' },
  { label: 'Findings', key: 'findings' },
  { label: 'Requested By', key: 'requestedBy' },
  { label: 'Started At', key: 'startedAt' },
  { label: 'Actions', key: 'actions' },
];

export const HISTORICAL_SCAN_TABLE_HEADERS = [
  { label: 'Company', key: 'companyName' },
  { label: 'Dates requested', key: 'dateRequested' },
  { label: 'Status', key: 'status' },
  { label: 'Scanned Domains', key: 'scannedDomains' },
  { label: 'Findings', key: 'findings' },
  { label: 'Requested By', key: 'requestedBy' },
  { label: 'Started', key: 'started' },
  { label: 'Actions', key: 'actions' },
];

export const formatParam = (value: string | null) =>
  value ? [{ label: value, id: value }] : undefined;

export const getDefaultDateRange = (): [string, string] => {
  const endDate = new Date();
  const startDate = new Date();

  startDate.setMonth(endDate.getMonth() - 3);

  const formatStartDate = (date: Date) => {
    return date.toISOString().slice(0, 10) + 'T00:00:00';
  };

  const formatEndDate = (date: Date) => {
    return date.toISOString().slice(0, 10) + 'T23:59:59';
  };

  return [formatStartDate(startDate), formatEndDate(endDate)];
};

export const STATUS_OPTIONS = [
  { label: 'Pending', value: 'pending' },
  { label: 'Running', value: 'running' },
  { label: 'Completed', value: 'completed' },
  { label: 'Cancelled', value: 'cancelled' },
];

export const isValidDateRange = (start: string, end: string) => {
  const startDate = new Date(start);
  const endDate = new Date(end);
  const dayDifference =
    (endDate.getTime() - startDate.getTime()) / (1000 * 3600 * 24);
  return dayDifference <= 365;
};

export const chunkArray = (
  arr: (Keyword | string)[],
  size: number,
): (Keyword | string)[][] => {
  const result: (Keyword | string)[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    result.push(arr.slice(i, i + size));
  }
  return result;
};

export const getChipKey = (
  domain: string,
  type: string,
  index: number,
  label?: string,
) => `${domain}-${type}-${index}-${label ?? ''}`;

export const CHIP_WEIGHT_OPTIONS = [0.5, 0.8, 0.9, 0.95, 0.99, 1];
/** Same tiers, strongest first - the order weight dropdowns list them (WHEEL-382). */
export const CHIP_WEIGHT_OPTIONS_DESC = [...CHIP_WEIGHT_OPTIONS].sort(
  (a, b) => b - a,
);

export const getNormalizedRoles = (updatedUser: any) => {
  return updatedUser?.Companies?.map((company: any) => ({
    CompanyId: company?.Company?.Id,
    Roles: company?.Roles?.map((role: string) => {
      const [roleId, roleName] = role.split('|');
      return { Id: Number(roleId), Name: roleName };
    }),
  }));
};

export const DOMAIN_REGEX = /^[a-zA-Z0-9]{1,253}$/;

export const WEB_SOCKET_CHANNELS = {
  THREAT_STATUS_UPDATE: 'threat_status_update',
  ASSET_NETWORK_DATA: 'asset_network_data',
  KEYWORDS_UPDATED: 'keywords_updated',
  KEYWORDS_UPDATED_FAILED: 'keywords_updated_failed',
  BULK_TAKEDOWN: 'bulk_takedown',
  TAKEDOWN_STATUS_UPDATE: 'takedown_status_update',
};

export const updateCompanyRoleFormate = (str: string) => {
  const frags = str.split(/[-_]/);
  for (let i = 0; i < frags.length; i++) {
    frags[i] = frags[i].charAt(0).toUpperCase() + frags[i].slice(1);
  }
  return frags.join(' ');
};

export { hashApiKey } from './hashApiKey';
